import { PostHelp } from "@/components/ai/PostHelp";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PostButtonEditor } from "@/components/PostButton";
import { FormatButtons } from "@/components/RichText";
import { BarChart3, ImagePlus, Loader2, Send, X, Film, Link2 } from "lucide-react";
import { uploadChunked, videoDuration, videoPoster } from "@/lib/storyUpload";
import { PollEditor } from "@/components/PollEditor";
import { EmojiButton } from "@/components/EmojiButton";
import { toast } from "sonner";
import { successPop } from "@/lib/celebrate";
import { api, formatErr, imgSrc } from "@/lib/api";
import { uploadImage } from "@/lib/imageUpload";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";

const MIN_CHARS = 30;
const MAX = 4;

function useImagePicker() {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const add = async (files) => {
    const list = [...files].slice(0, MAX - images.length);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const { url } = await uploadImage("/feed/upload", f);
        setImages((l) => [...l, url]);
      }
    } catch (e) { toast.error(formatErr(e)); } finally { setUploading(false); }
  };
  return { images, setImages, uploading, add, remove: (u) => setImages((l) => l.filter((x) => x !== u)) };
}

const Previews = ({ images, onRemove }) => (
  <div className="grid grid-cols-4 gap-2" data-testid="composer-previews">
    {images.map((u) => (
      <div key={u} className="relative aspect-square rounded-xl overflow-hidden bg-rose-50">
        <img src={imgSrc(u)} alt="" className="w-full h-full object-cover" />
        <button onClick={() => onRemove(u)} data-testid="composer-remove-image" className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
      </div>
    ))}
  </div>
);

export function PostComposer({ onPosted }) {
  const { user, refreshUser } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const textRef = useRef(null);
  const [unboxing, setUnboxing] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("compose") !== "unboxing") return;
    setText("Meine Mystery-Box von Saskia & Stefan ist da! 🎁✨ Das war drin: ");
    setUnboxing(true);
    params.delete("compose"); setParams(params, { replace: true });
    setTimeout(() => { const t = textRef.current; if (t) { t.scrollIntoView({ behavior: "smooth", block: "center" }); t.focus(); } }, 400);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = useImagePicker();
  const [poll, setPoll] = useState(null);
  const [vid, setVid] = useState(null);
  const vidRef = useRef(null);
  const isAdmin = user?.role === "admin";
  const isStaff = isAdmin || user?.role === "moderator";
  const [btn, setBtn] = useState(null);
  const vidBusy = vid && !vid.url;
  const addVideo = async (file) => {
    if (!file) return;
    if (!["video/mp4", "video/quicktime", "video/webm"].includes(file.type)) { toast.error("Bitte ein MP4-, MOV- oder WEBM-Video wählen"); return; }
    if (file.size > 200 * 1024 * 1024) { toast.error("Video ist zu groß (max. 200 MB)"); return; }
    setVid({ progress: 0 });
    try {
      const duration = await videoDuration(file);
      const poster = videoPoster(file).then((f) => (f ? uploadImage("/feed/upload", f).then((d) => d.url) : null)).catch(() => null);
      const url = await uploadChunked("/feed/upload/video", file, (n) => setVid((v) => v && { ...v, progress: n }));
      setVid({ url, progress: 100, duration: duration || null, poster: await poster });
    }
    catch (e) { setVid(null); toast.error(formatErr(e)); }
  };
  const pollOpts = poll ? poll.options.map((o) => o.trim()).filter(Boolean) : [];
  const len = text.trim().length;
  const short = len < MIN_CHARS;
  const pollBad = poll && (pollOpts.length < 2 || !text.trim());

  const submit = async () => {
    if (short || busy || pick.uploading || vidBusy) return;
    if (pollBad) { toast.error("Für eine Umfrage brauchst du eine Frage und mindestens 2 Antworten"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/feed/posts", { text, images: pick.images, ...(vid?.url ? { video_file: vid.url, video_duration: vid.duration, video_poster: vid.poster || undefined } : {}), ...(poll ? { poll: { options: pollOpts, days: poll.days }, pin: isAdmin && !!poll.pin } : {}), ...(unboxing ? { unboxing: true } : {}), ...(btn && btn.label.trim() ? { button: { url: btn.url, label: btn.label.trim() } } : {}) });
      onPosted(data); setUnboxing(false); setBtn(null); setText(""); pick.setImages([]); setPoll(null); setVid(null); successPop(document.querySelector('[data-testid="post-composer"]'));
      toast.success(data.unboxing_bonus ? `Danke fürs Unboxing! +${data.points_earned} Punkte inkl. ${data.unboxing_bonus} Bonus 🎁` : data.points_earned ? `Gepostet! +${data.points_earned} Punkte ✨` : "Gepostet!");
      refreshUser();
      window.dispatchEvent(new Event("points-changed"));
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-3xl p-4 sm:p-5 border border-pink-100 space-y-3" data-testid="post-composer">
      <div className="flex gap-3">
        <UserAvatar userId={user?.user_id} name={user?.name} picture={user?.picture} size="w-10 h-10" />
        <textarea ref={textRef} value={text} onChange={(e) => setText(e.target.value)} maxLength={isAdmin ? 5000 : 2000} rows={2} data-testid="composer-input"
          placeholder={poll ? "Stell deine Frage …" : `Was gibt's Neues, ${user?.name?.split(" ")[0] || ""}?`}
          className="flex-1 min-w-0 resize-none bg-transparent text-base placeholder:text-slate-400 focus:outline-none pt-2" />
      </div>
      {len > 0 && short && <p className="pl-[3.25rem] text-xs font-semibold text-amber-600" data-testid="composer-min-hint">Noch {MIN_CHARS - len} Zeichen – Beiträge brauchen mindestens {MIN_CHARS} Zeichen.</p>}
      {unboxing && <p className="pl-[3.25rem] text-xs font-semibold text-amber-700 flex items-center gap-1.5" data-testid="composer-unboxing-hint">🎁 Unboxing-Beitrag – füge ein Foto hinzu, dann erscheint er in der Unboxing-Galerie. Für dein erstes Unboxing-Foto gibt es 50 Bonus-Punkte!</p>}
      {pick.images.length > 0 && <Previews images={pick.images} onRemove={pick.remove} />}
      {vid && (
        <div className="relative rounded-2xl overflow-hidden bg-slate-900" data-testid="composer-video-preview">
          {vid.url ? <video src={`${process.env.REACT_APP_BACKEND_URL}${vid.url}${vid.poster ? "" : "#t=0.5"}`} poster={vid.poster ? imgSrc(vid.poster) : undefined} controls playsInline preload="metadata" className="w-full max-h-80" data-testid="composer-video-player" />
            : <div className="h-32 flex flex-col items-center justify-center gap-2 text-white text-sm font-semibold"><Loader2 className="w-6 h-6 animate-spin" /> Video wird hochgeladen … {vid.progress}%
                <div className="w-2/3 h-1.5 rounded-full bg-white/20 overflow-hidden"><div className="h-full pink-gradient transition-[width]" style={{ width: `${vid.progress}%` }} /></div></div>}
          {vid.url && <button onClick={() => setVid(null)} data-testid="composer-video-remove" aria-label="Video entfernen" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>}
        </div>
      )}
      {btn && <PostButtonEditor value={btn} onChange={setBtn} />}
      {poll && <PollEditor poll={poll} setPoll={setPoll} onRemove={() => setPoll(null)} canPin={isAdmin} />}
      <div className="flex flex-wrap items-center gap-x-0.5 gap-y-1 sm:gap-2 pt-1 border-t border-pink-50">
        <button onClick={() => fileRef.current?.click()} disabled={pick.uploading || pick.images.length >= MAX} data-testid="composer-add-image"
          className="inline-flex items-center gap-2 px-2.5 sm:px-3 h-10 rounded-full text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40">
          {pick.uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-5 h-5" />} <span className="hidden sm:inline whitespace-nowrap">Bild ({pick.images.length}/{MAX})</span>
        </button>
        {isAdmin && <button onClick={() => vidRef.current?.click()} disabled={!!vid} data-testid="composer-add-video"
          className="inline-flex items-center gap-2 px-2.5 sm:px-3 h-10 rounded-full text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40">
          <Film className="w-5 h-5" /> <span className="hidden sm:inline">Video</span>
        </button>}
        {isAdmin && <input ref={vidRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" data-testid="composer-video-input" onChange={(e) => { addVideo(e.target.files[0]); e.target.value = ""; }} />}
        <EmojiButton inputRef={textRef} value={text} onChange={setText} testId="composer-emoji" />
        {!poll && <PostHelp text={text} onText={setText} />}
        {isAdmin && <FormatButtons inputRef={textRef} value={text} onChange={setText} />}
        {isStaff && <button onClick={() => setBtn((b) => b || { url: "/community-unterstuetzen", label: "Zu Projekt unterstützen" })} disabled={!!btn} data-testid="composer-add-button"
          className="inline-flex items-center gap-2 px-2.5 sm:px-3 h-10 rounded-full text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40">
          <Link2 className="w-5 h-5" /> <span className="hidden sm:inline">Button</span>
        </button>}
        <button onClick={() => setPoll((p) => p || { options: ["", ""], days: 3 })} disabled={!!poll} data-testid="composer-add-poll"
          className="inline-flex items-center gap-2 px-2.5 sm:px-3 h-10 rounded-full text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40 mr-auto">
          <BarChart3 className="w-5 h-5" /> <span className="hidden sm:inline">Umfrage</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="composer-file-input" onChange={(e) => { pick.add(e.target.files); e.target.value = ""; }} />
        <button onClick={submit} disabled={busy || pick.uploading || vidBusy || short || pollBad} data-testid="composer-submit"
          className="ml-auto inline-flex items-center gap-2 px-4 sm:px-5 h-10 rounded-full pink-gradient text-white text-sm font-bold disabled:opacity-50 active:scale-95 transition-transform">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Posten
        </button>
      </div>
    </div>
  );
}
